'use client';

/**
 * NotificationsOverlay Component
 *
 * Full-viewport notifications center triggered from the business-portal topbar
 * (Bell button or ⌘N). Replaces the small popover for the dedicated focus
 * surface — filter tabs + grouped-by-date list + mark-all-read.
 *
 * Behavior:
 *   - 5 filter tabs: `All` / `Unread` / `Tasks` / `Approvals` / `Mentions`.
 *   - List grouped by date bucket (`Today` / `Yesterday` / older relative
 *     labels), each group has a sticky header inside the scrollable region.
 *   - "Mark all read" button (header-right). Disabled when no unread.
 *   - Esc and backdrop click close.
 *
 * Reference: `.claude/design-handoff-v3/project/pages/auth-overlays.jsx`
 * `OverlayNotif` (full-viewport variant of `NotificationsPopover`).
 *
 * §11b: Glass primitives only. §11b2: Typography variants only.
 * §13: All copy via `useI18n` + `t()`.
 */

import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import DoneAllRoundedIcon from '@mui/icons-material/DoneAllRounded';
import KeyboardCommandKeyIcon from '@mui/icons-material/KeyboardCommandKey';
import NotificationsNoneRoundedIcon from '@mui/icons-material/NotificationsNoneRounded';
import Box from '@mui/material/Box';
import Modal from '@mui/material/Modal';
import Paper from '@mui/material/Paper';
import { alpha, type SxProps, type Theme, useTheme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import { type ReactNode, useId, useMemo, useState } from 'react';
import { useI18n } from '../../hooks';
import { iconSizes } from '../../theme/typography';
import { GlassButton } from '../GlassButton';
import { GlassChip } from '../GlassChip';
import { GlassIconButton } from '../GlassIconButton';
import { GlassTabs } from '../GlassTabs';
import type { NotificationItemData } from '../NotificationItem';

// ============================================================================
// Types
// ============================================================================

export type NotificationsOverlayFilter = 'all' | 'unread' | 'tasks' | 'approvals' | 'mentions';

export interface NotificationsOverlayProps {
  /** Whether the overlay is open. */
  open: boolean;
  /** Close handler — fires on Esc, backdrop click, and the X button. */
  onClose: () => void;
  /** Notifications to display. */
  notifications: NotificationItemData[];
  /** Whether the list is loading. */
  loading?: boolean;
  /** Mark-all-read handler — disabled when there are no unread items. */
  onMarkAllRead?: () => void;
  /** Per-row click handler. */
  onClickNotification?: (id: string) => void;
  /** Optional initial filter tab. Default `all`. */
  initialFilter?: NotificationsOverlayFilter;
  /** Optional empty-state node when the active filter matches zero items. */
  emptyState?: ReactNode;
  /** Style overrides. */
  sx?: SxProps<Theme>;
}

// ============================================================================
// Helpers
// ============================================================================

const MONO = 'ui-monospace, "SF Mono", Menlo, Monaco, Consolas, monospace';

interface GroupedNotifications {
  /** Localized group label (e.g., "Today", "Yesterday"). */
  label: string;
  items: NotificationItemData[];
}

/**
 * Bucket notifications by relative date:
 *   - Today / Yesterday / This week / Earlier
 *
 * Stable ordering — newest first within each bucket. Localized labels come
 * from the caller (passed via t()) so this fn returns BUCKET KEYS, not
 * pre-formatted strings.
 */
function bucketByDate(items: NotificationItemData[]): Array<{ key: string; items: NotificationItemData[] }> {
  const now = new Date();
  const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate());
  const startOfYesterday = new Date(startOfToday);
  startOfYesterday.setDate(startOfYesterday.getDate() - 1);
  const startOfThisWeek = new Date(startOfToday);
  startOfThisWeek.setDate(startOfThisWeek.getDate() - 7);

  type BucketKey = 'today' | 'yesterday' | 'thisWeek' | 'earlier';
  const buckets: Record<BucketKey, NotificationItemData[]> = {
    today: [],
    yesterday: [],
    thisWeek: [],
    earlier: [],
  };

  for (const item of items) {
    const ts = typeof item.createdAt === 'string' ? new Date(item.createdAt) : item.createdAt;
    if (Number.isNaN(ts.getTime())) {
      buckets.earlier.push(item);
      continue;
    }
    if (ts >= startOfToday) buckets.today.push(item);
    else if (ts >= startOfYesterday) buckets.yesterday.push(item);
    else if (ts >= startOfThisWeek) buckets.thisWeek.push(item);
    else buckets.earlier.push(item);
  }

  // Sort newest-first within each bucket. createdAt missing/invalid sorts
  // to the bottom.
  const sortDesc = (a: NotificationItemData, b: NotificationItemData) => {
    const ta = typeof a.createdAt === 'string' ? new Date(a.createdAt).getTime() : a.createdAt.getTime();
    const tb = typeof b.createdAt === 'string' ? new Date(b.createdAt).getTime() : b.createdAt.getTime();
    return (Number.isNaN(tb) ? -Infinity : tb) - (Number.isNaN(ta) ? -Infinity : ta);
  };
  Object.values(buckets).forEach((arr) => arr.sort(sortDesc));

  return (['today', 'yesterday', 'thisWeek', 'earlier'] as const satisfies readonly BucketKey[])
    .filter((key) => buckets[key].length > 0)
    .map((key) => ({ key, items: buckets[key] }));
}

/**
 * Filter notifications by tab.
 *   - 'unread'    → not yet read
 *   - 'tasks'     → type === 'task'
 *   - 'approvals' → type === 'approval'
 *   - 'mentions'  → isMention === true
 *   - 'all'       → everything
 *
 * 'tasks' / 'approvals' are best-effort: the backend uses string types and
 * may not have set the canonical values everywhere yet, in which case the
 * tab degrades to an empty-state.
 */
function filterByTab(items: NotificationItemData[], filter: NotificationsOverlayFilter): NotificationItemData[] {
  switch (filter) {
    case 'unread':
      return items.filter((n) => !n.isRead);
    case 'tasks':
      return items.filter((n) => String(n.type).toLowerCase() === 'task');
    case 'approvals':
      return items.filter((n) => String(n.type).toLowerCase() === 'approval');
    case 'mentions':
      return items.filter((n) => n.isMention === true);
    default:
      return items;
  }
}

/**
 * Format the timestamp as a relative chip: "8m", "1h", "Wed".
 */
function formatRelative(date: Date | string): string {
  const ts = typeof date === 'string' ? new Date(date) : date;
  if (Number.isNaN(ts.getTime())) return '';
  const diff = Date.now() - ts.getTime();
  const minutes = Math.floor(diff / 60_000);
  if (minutes < 1) return 'now';
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h`;
  const days = Math.floor(hours / 24);
  if (days < 7) return `${days}d`;
  return ts.toLocaleDateString('en-US', { weekday: 'short' });
}

// ============================================================================
// Component
// ============================================================================

export function NotificationsOverlay({
  open,
  onClose,
  notifications,
  loading = false,
  onMarkAllRead,
  onClickNotification,
  initialFilter = 'all',
  emptyState,
  sx,
}: NotificationsOverlayProps) {
  const theme = useTheme();
  const { t } = useI18n();
  const titleId = useId();
  const isDark = theme.palette.mode === 'dark';

  const [filter, setFilter] = useState<NotificationsOverlayFilter>(initialFilter);

  const unreadCount = useMemo(() => notifications.filter((n) => !n.isRead).length, [notifications]);
  const filtered = useMemo(() => filterByTab(notifications, filter), [notifications, filter]);
  const groups: GroupedNotifications[] = useMemo(() => {
    const bucketKeyToLabel: Record<string, string> = {
      today: t('overlays.notifications.bucket.today'),
      yesterday: t('overlays.notifications.bucket.yesterday'),
      thisWeek: t('overlays.notifications.bucket.thisWeek'),
      earlier: t('overlays.notifications.bucket.earlier'),
    };
    return bucketByDate(filtered).map(({ key, items }) => ({
      label: bucketKeyToLabel[key] ?? key,
      items,
    }));
  }, [filtered, t]);

  const tabs = useMemo(
    () => [
      { value: 'all', label: t('overlays.notifications.tabs.all'), count: notifications.length },
      { value: 'unread', label: t('overlays.notifications.tabs.unread'), count: unreadCount },
      {
        value: 'tasks',
        label: t('overlays.notifications.tabs.tasks'),
        count: filterByTab(notifications, 'tasks').length,
      },
      {
        value: 'approvals',
        label: t('overlays.notifications.tabs.approvals'),
        count: filterByTab(notifications, 'approvals').length,
      },
      {
        value: 'mentions',
        label: t('overlays.notifications.tabs.mentions'),
        count: filterByTab(notifications, 'mentions').length,
      },
    ],
    [notifications, unreadCount, t],
  );

  return (
    <Modal
      open={open}
      onClose={onClose}
      aria-labelledby={titleId}
      slotProps={{
        backdrop: {
          sx: {
            backgroundColor: alpha(theme.palette.background.default, 0.66),
            backdropFilter: 'blur(8px) saturate(140%)',
            WebkitBackdropFilter: 'blur(8px) saturate(140%)',
          },
        },
      }}
      sx={{
        display: 'flex',
        alignItems: { xs: 'flex-start', md: 'center' },
        justifyContent: 'center',
        p: { xs: 2, md: 4 },
        zIndex: theme.zIndex.modal,
      }}
    >
      <Paper
        role="dialog"
        aria-labelledby={titleId}
        elevation={0}
        sx={[
          {
            width: '100%',
            maxWidth: 760,
            maxHeight: { xs: '100%', md: 'calc(100vh - 64px)' },
            display: 'flex',
            flexDirection: 'column',
            borderRadius: { xs: '12px', md: '14px' },
            overflow: 'hidden',
            backgroundColor: 'background.paper',
            border: '1px solid',
            borderColor: 'divider',
            boxShadow: theme.shadows[16],
          },
          ...(Array.isArray(sx) ? sx : [sx]),
        ]}
      >
        {/* Header */}
        <Box
          sx={{
            px: 2.5,
            py: 1.75,
            borderBottom: '1px solid',
            borderColor: 'divider',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'space-between',
            gap: 1.25,
            flexShrink: 0,
          }}
        >
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1.25, minWidth: 0 }}>
            <NotificationsNoneRoundedIcon aria-hidden sx={{ fontSize: iconSizes.md, color: 'warning.main' }} />
            <Typography
              id={titleId}
              variant="h4"
              sx={{
                fontWeight: 600,
                color: 'text.primary',
                whiteSpace: 'nowrap',
                overflow: 'hidden',
                textOverflow: 'ellipsis',
              }}
            >
              {t('overlays.notifications.title')}
            </Typography>
            {unreadCount > 0 && (
              <GlassChip
                label={t('overlays.notifications.unreadChip', { count: unreadCount })}
                size="small"
                color="warning"
                aria-label={t('overlays.notifications.unreadChip', { count: unreadCount })}
                sx={{ height: 20 }}
              />
            )}
          </Box>
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexShrink: 0 }}>
            <GlassButton
              variant="ghost"
              size="small"
              startIcon={<DoneAllRoundedIcon sx={{ fontSize: iconSizes.sm }} aria-hidden />}
              onClick={onMarkAllRead}
              disabled={unreadCount === 0 || !onMarkAllRead}
              aria-disabled={unreadCount === 0 || !onMarkAllRead}
            >
              {t('overlays.notifications.markAllRead')}
            </GlassButton>
            <Box
              aria-hidden
              sx={{
                display: { xs: 'none', md: 'flex' },
                alignItems: 'center',
                gap: 0.5,
                px: 0.75,
                py: 0.25,
                borderRadius: '6px',
                border: '1px solid',
                borderColor: 'divider',
                color: 'text.disabled',
              }}
            >
              <KeyboardCommandKeyIcon sx={{ fontSize: iconSizes['2xs'] }} />
              <Typography variant="overline" sx={{ fontFamily: MONO, letterSpacing: '0.08em', lineHeight: 1 }}>
                N
              </Typography>
            </Box>
            <GlassIconButton size="small" onClick={onClose} aria-label={t('overlays.notifications.close')}>
              <CloseRoundedIcon sx={{ fontSize: iconSizes.md }} />
            </GlassIconButton>
          </Box>
        </Box>

        {/* Filter tabs */}
        <Box
          sx={{
            px: 2.5,
            py: 1,
            borderBottom: '1px solid',
            borderColor: 'divider',
            flexShrink: 0,
            backgroundColor: 'background.default',
          }}
        >
          <GlassTabs
            value={filter}
            onChange={(_event, next) => setFilter(String(next) as NotificationsOverlayFilter)}
            tabs={tabs.map((tab) => ({
              value: tab.value,
              label: tab.count > 0 ? `${tab.label} · ${tab.count}` : tab.label,
            }))}
          />
        </Box>

        {/* List */}
        <Box sx={{ flex: 1, overflow: 'auto' }}>
          {loading ? (
            <Box sx={{ p: 3, textAlign: 'center' }}>
              <Typography variant="caption" sx={{ color: 'text.disabled' }}>
                {t('overlays.notifications.loading')}
              </Typography>
            </Box>
          ) : filtered.length === 0 ? (
            <Box sx={{ p: 4, textAlign: 'center' }}>
              {emptyState ?? (
                <Typography variant="body2" sx={{ color: 'text.disabled' }}>
                  {t('overlays.notifications.empty')}
                </Typography>
              )}
            </Box>
          ) : (
            groups.map((group) => (
              <Box key={group.label} component="section" aria-label={group.label}>
                <Box
                  sx={{
                    position: 'sticky',
                    top: 0,
                    zIndex: 1,
                    px: 2.5,
                    py: 1,
                    backgroundColor: 'background.default',
                    borderBottom: '1px solid',
                    borderColor: 'divider',
                  }}
                >
                  <Typography
                    variant="overline"
                    sx={{
                      fontFamily: MONO,
                      color: 'text.disabled',
                      letterSpacing: '0.08em',
                      fontWeight: 600,
                    }}
                  >
                    {group.label}
                  </Typography>
                </Box>
                {group.items.map((notification) => (
                  <NotificationRow
                    key={notification.id}
                    notification={notification}
                    onClick={onClickNotification}
                    isDark={isDark}
                  />
                ))}
              </Box>
            ))
          )}
        </Box>
      </Paper>
    </Modal>
  );
}

NotificationsOverlay.displayName = 'NotificationsOverlay';

// ============================================================================
// Sub-components
// ============================================================================

function NotificationRow({
  notification,
  onClick,
  isDark,
}: {
  notification: NotificationItemData;
  onClick?: (id: string) => void;
  isDark: boolean;
}) {
  const theme = useTheme();
  const sev = String(notification.severity).toLowerCase();
  const tone: 'critical' | 'warning' | 'info' =
    sev === 'critical' || sev === 'error' || sev === 'urgent'
      ? 'critical'
      : sev === 'warning' || sev === 'warn'
        ? 'warning'
        : 'info';
  const accentColor =
    tone === 'critical'
      ? theme.palette.error.main
      : tone === 'warning'
        ? theme.palette.warning.main
        : theme.palette.success.main;
  const isClickable = !!onClick;
  const handleClick = () => {
    if (onClick) onClick(notification.id);
  };

  return (
    <Box
      {...(isClickable
        ? { component: 'button' as const, type: 'button' as const, onClick: handleClick }
        : { component: 'div' as const })}
      sx={{
        width: '100%',
        px: 2.5,
        py: 1.5,
        display: 'flex',
        alignItems: 'flex-start',
        gap: 1.25,
        textAlign: 'left',
        cursor: isClickable ? 'pointer' : 'default',
        fontFamily: 'inherit',
        color: 'inherit',
        backgroundColor: notification.isRead ? 'transparent' : alpha(accentColor, isDark ? 0.04 : 0.03),
        borderBottom: '1px solid',
        borderColor: 'divider',
        borderLeft: notification.isRead ? '3px solid transparent' : `3px solid ${accentColor}`,
        transition: 'background-color 120ms ease',
        minHeight: 44,
        '&:hover': isClickable ? { backgroundColor: alpha(accentColor, isDark ? 0.1 : 0.06) } : undefined,
        '&:focus-visible': {
          outline: `2px solid ${accentColor}`,
          outlineOffset: -2,
        },
      }}
    >
      <Box sx={{ flex: 1, minWidth: 0 }}>
        <Typography
          variant="body2"
          sx={{
            fontWeight: notification.isRead ? 400 : 500,
            color: 'text.primary',
            lineHeight: 1.4,
          }}
        >
          {notification.title}
        </Typography>
        {notification.message && (
          <Typography
            variant="caption"
            sx={{
              display: '-webkit-box',
              color: 'text.secondary',
              lineHeight: 1.4,
              mt: 0.25,
              overflow: 'hidden',
              textOverflow: 'ellipsis',
              WebkitLineClamp: 2,
              WebkitBoxOrient: 'vertical',
            }}
          >
            {notification.message}
          </Typography>
        )}
      </Box>
      <Typography
        variant="caption"
        sx={{
          fontFamily: MONO,
          color: 'text.disabled',
          flexShrink: 0,
          mt: 0.25,
        }}
      >
        {formatRelative(notification.createdAt)}
      </Typography>
    </Box>
  );
}
